import React, { useMemo, useState } from 'react';
import {
    Box,
    Card,
    Chip,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Order, OrderStatus } from '@api/Orders/Order';
import {
    BOARD_COLUMNS,
    OrderChannelKey,
    formatBoardClock,
    getBoardClock,
    getBoardColumn,
    getOrderChannel,
    getOrderShortNumber
} from '@utils/OrdersBoard';
import useTimer from '@hooks/useTimer';
import OrderBoardCard, { BoardActionKind, CHANNEL_ICONS, CHANNEL_LABELS } from './OrderBoardCard';
import OrdersViewSwitch, { OrdersView } from './OrdersViewSwitch';
import { toMillisSafe } from '@utils/Date';

const CHANNEL_KEYS: OrderChannelKey[] = ['mostrador', 'kiosko', 'mesa', 'pickup', 'delivery'];

// ============================================================================
// MODO COMPACTO (2026-08-19, pedido de Irving: en pantallas chicas los filtros y
// la barra de Entregados estorban).
//
// El problema medido en una tablet de 1024×768 (≈630px útiles bajo la barra de
// 70px): la fila de filtros se lleva hasta 86px cuando envuelve, Entregados hasta
// ~130px cuando los chips envuelven, y entre paddings y separaciones quedan ~380px
// para las columnas. Con tarjetas de ~250px se ven 1.5 pedidos.
//
// Un SOLO interruptor, no tres: la tablet de cocina se configura una vez. Recorta
// las cuatro cosas a la vez (filtros a una fila de iconos, Entregados colapsado,
// tarjetas densas, encabezados delgados) y se recuerda POR DISPOSITIVO — igual que
// Lista/Tablero, porque la tablet de cocina y la laptop del gerente no quieren lo
// mismo y no hay razón para que una decida por la otra.
// ============================================================================
export const BOARD_COMPACT_STORAGE_KEY = 'krispis.ordersBoardCompact';

export const getStoredBoardCompact = (): boolean => {
    try {
        return localStorage.getItem(BOARD_COMPACT_STORAGE_KEY) === '1';
    } catch (error) {
        return false;
    }
};

const storeBoardCompact = (compact: boolean) => {
    try {
        localStorage.setItem(BOARD_COMPACT_STORAGE_KEY, compact ? '1' : '0');
    } catch (error) {
        // Modo privado o storage lleno: el modo simplemente no se recuerda
    }
};

/** Valor del botón "Todos" del filtro de canal (sin filtro aplicado) */
const ALL_CHANNELS = 'todos';

const today = new Date();
today.setHours(0, 0, 0, 0);

// Ordena el tablero. Tuvo su propia versión que devolvía 0 para un Timestamp
// crudo (mandaba el pedido al extremo del orden); hoy es la del util.
const toMillis = (value: any): number => toMillisSafe(value);

interface OrdersBoardViewProps {
    orders: Order[];
    search: string;
    onChangeSearch: (value: string) => void;
    view: OrdersView;
    onChangeView: (view: OrdersView) => void;
    /** Solo admin puede dar por pagado un pedido de la web (misma regla que la lista) */
    canMarkPaid: boolean;
    onAction: (order: Order, kind: BoardActionKind) => void;
}

const OrdersBoardView: React.FunctionComponent<OrdersBoardViewProps> = (props) => {
    const { orders, search, onChangeSearch, view, onChangeView, canMarkPaid, onAction } = props;

    const [channelFilter, setChannelFilter] = useState<OrderChannelKey | null>(null);
    const [expandedOrders, setExpandedOrders] = useState<string[]>([]);
    const [compact, setCompact] = useState<boolean>(getStoredBoardCompact);
    // Entregados arranca cerrado en compacto y abierto en normal (como siempre)
    const [completedOpen, setCompletedOpen] = useState<boolean>(() => !getStoredBoardCompact());
    // En compacto el buscador vive detrás de la lupa; si hay texto se queda a la
    // vista, o el filtro quedaría activo sin manera de verlo ni quitarlo.
    const [searchOpen, setSearchOpen] = useState(false);

    const onToggleCompact = () => {
        const next = !compact;
        setCompact(next);
        storeBoardCompact(next);
        setCompletedOpen(!next);
        if (!next) setSearchOpen(false);
    };

    const showSearchField = !compact || searchOpen || !!search;

    // El tablero es un reloj: se repinta cada segundo para mover el semáforo
    useTimer(today);
    const now = new Date();

    const visibleOrders = useMemo(
        () => (channelFilter ? orders.filter((order) => getOrderChannel(order).key === channelFilter) : orders),
        [orders, channelFilter]
    );

    const columns = useMemo(
        () =>
            BOARD_COLUMNS.map((column) => ({
                ...column,
                // FIFO: lo más viejo hasta arriba, que es lo siguiente que hay que sacar
                orders: visibleOrders
                    .filter((order) => getBoardColumn(order) === column.key)
                    .sort((a, b) => toMillis(a.date) - toMillis(b.date))
            })),
        [visibleOrders]
    );

    const completedOrders = useMemo(
        () =>
            visibleOrders
                .filter((order) => order.status === OrderStatus.Completed)
                .sort((a, b) => toMillis(b.lastUpdate) - toMillis(a.lastUpdate))
                .slice(0, 8),
        [visibleOrders]
    );

    const onToggleExpand = (orderId: string) =>
        setExpandedOrders((current) =>
            current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId]
        );

    return (
        <Stack
            height={{ xs: 'auto', md: '100%' }}
            minHeight={0}
            p={compact ? 1 : 2}
            spacing={compact ? 1 : 2}
            bgcolor="background.default"
            // CssBaseline solo se monta en las pantallas de login, así que aquí
            // box-sizing es content-box: sin esto el padding se SUMA al 100% y la
            // pantalla termina 32px más alta, con scroll de página.
            boxSizing="border-box"
        >
            {/* Buscador + cambio de vista + filtro de canal. En compacto todo cabe en
                UNA fila: el buscador se esconde tras la lupa y los canales quedan
                como iconos (con su nombre accesible, que es el mismo botón). */}
            <Stack
                direction="row"
                spacing={compact ? 1 : 2}
                alignItems="center"
                flexWrap={compact ? 'nowrap' : 'wrap'}
                useFlexGap
                minWidth={0}
                sx={compact ? { overflowX: 'auto' } : undefined}
            >
                {showSearchField ? (
                    <TextField
                        size="small"
                        placeholder="Buscar pedido"
                        value={search}
                        autoFocus={compact && searchOpen}
                        onChange={(event) => onChangeSearch(event.target.value)}
                        onBlur={() => !search && setSearchOpen(false)}
                        sx={{ width: compact ? 180 : { xs: '100%', sm: 260 }, flexShrink: 0 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                    />
                ) : (
                    <Tooltip title="Buscar pedido">
                        <IconButton aria-label="Buscar pedido" onClick={() => setSearchOpen(true)}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                )}
                <OrdersViewSwitch view={view} onChange={onChangeView} compact={compact} />
                <Box flex={1} />
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={channelFilter ?? ALL_CHANNELS}
                    onChange={(_, value: OrderChannelKey | typeof ALL_CHANNELS | null) =>
                        setChannelFilter(!value || value === ALL_CHANNELS ? null : value)
                    }
                    sx={{
                        flexShrink: 0,
                        '& .MuiToggleButton-root': {
                            px: compact ? 1 : 1.5,
                            py: compact ? 0.75 : 1,
                            textTransform: 'none',
                            gap: 0.75
                        }
                    }}
                >
                    <ToggleButton value={ALL_CHANNELS}>Todos</ToggleButton>
                    {CHANNEL_KEYS.map((key) => (
                        <ToggleButton key={key} value={key} aria-label={CHANNEL_LABELS[key]}>
                            <Tooltip title={compact ? CHANNEL_LABELS[key] : ''}>{CHANNEL_ICONS[key]}</Tooltip>
                            {!compact && CHANNEL_LABELS[key]}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Tooltip title={compact ? 'Salir del modo compacto' : 'Modo compacto: más pedidos por pantalla'}>
                    <ToggleButton
                        size="small"
                        value="compact"
                        selected={compact}
                        aria-label="Modo compacto"
                        onChange={onToggleCompact}
                        sx={{ flexShrink: 0, px: compact ? 1 : 1.5, py: compact ? 0.75 : 1, textTransform: 'none' }}
                    >
                        <DensitySmallIcon sx={{ fontSize: 20 }} />
                        {!compact && (
                            <Box component="span" ml={0.75}>
                                Compacto
                            </Box>
                        )}
                    </ToggleButton>
                </Tooltip>
            </Stack>

            {/* Columnas. Van con Stack y NO con Grid: el spacing de Grid mete padding
                dentro de cada item, así que una columna con height 100% termina más
                alta que su contenedor y las tarjetas de abajo se cortan en vez de
                scrollear. Con Stack la altura del contenedor manda y el scroll vive
                dentro de cada columna (regla de layout del proyecto: sin scroll de página). */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={compact ? 1 : 2} flex={1} minHeight={0}>
                {columns.map((column) => {
                    const oldest = column.orders.reduce(
                        (max, order) => Math.max(max, getBoardClock(order, now).minutes),
                        0
                    );
                    return (
                        <Stack
                            key={column.key}
                            component={Card}
                            p={0}
                            flex={1}
                            minWidth={0}
                            minHeight={0}
                            height={{ xs: 'auto', md: '100%' }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                px={compact ? 1.5 : 2}
                                py={compact ? 0.75 : 1.5}
                            >
                                <Typography
                                    variant={compact ? 'subtitle2' : 'subtitle1'}
                                    fontWeight={700}
                                    textTransform="uppercase"
                                    noWrap
                                >
                                    {column.title}
                                </Typography>
                                <Chip size="small" label={column.orders.length} />
                                <Box flex={1} />
                                {column.orders.length > 0 && (
                                    // En compacto el rótulo "más antiguo" se va y queda el número,
                                    // que es el dato: el encabezado pasa de dos renglones a uno.
                                    <Stack alignItems="flex-end">
                                        {!compact && (
                                            <Typography variant="caption" color="text.secondary">
                                                más antiguo
                                            </Typography>
                                        )}
                                        <Tooltip title={compact ? 'El más antiguo de la columna' : ''}>
                                            <Typography variant="body2" fontWeight={700}>
                                                {formatBoardClock(oldest)}
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                )}
                            </Stack>
                            <Divider />
                            <Stack
                                p={compact ? 1 : 1.5}
                                spacing={compact ? 1 : 1.5}
                                flex={1}
                                minHeight={0}
                                overflow="auto"
                            >
                                {column.orders.length === 0 ? (
                                    <Stack
                                        alignItems="center"
                                        justifyContent="center"
                                        py={compact ? 2 : 5}
                                        border={1}
                                        borderColor="divider"
                                        borderRadius={2}
                                        sx={{ borderStyle: 'dashed' }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            Sin pedidos
                                        </Typography>
                                    </Stack>
                                ) : (
                                    column.orders.map((order) => (
                                        <OrderBoardCard
                                            key={order.id}
                                            order={order}
                                            now={now}
                                            expanded={expandedOrders.includes(order.id as string)}
                                            canMarkPaid={canMarkPaid}
                                            compact={compact}
                                            onToggleExpand={onToggleExpand}
                                            onAction={onAction}
                                        />
                                    ))
                                )}
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>

            {/* Entregados: rastro de lo que ya salió (se abre en la lista para revisarlo).
                Se PUEDE COLAPSAR y no se elimina: sirve para "¿ya salió el 42?", que es
                una pregunta real de la barra. Cerrado deja un renglón de ~32px en vez de
                los ~130px que ocupaba cuando los chips envolvían. */}
            {completedOrders.length > 0 && (
                <Stack component={Card} p={compact ? 0.75 : 1.5} flexShrink={0}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        onClick={() => setCompletedOpen((open) => !open)}
                        sx={{ cursor: 'pointer' }}
                    >
                        <IconButton
                            size="small"
                            aria-label={completedOpen ? 'Ocultar entregados' : 'Ver entregados'}
                            sx={{ p: 0.25 }}
                        >
                            {completedOpen ? (
                                <ExpandMoreIcon fontSize="small" />
                            ) : (
                                <ChevronRightIcon fontSize="small" />
                            )}
                        </IconButton>
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            textTransform="uppercase"
                            color="text.secondary"
                        >
                            Entregados
                        </Typography>
                        <Chip size="small" label={completedOrders.length} />
                    </Stack>
                    {/* Render condicional y no `Collapse`: cerrado tiene que dejar de
                        ocupar alto YA — en una tablet esos 100px son una tarjeta. */}
                    {completedOpen && (
                        <Stack direction="row" alignItems="center" spacing={1} pt={1} flexWrap="wrap" useFlexGap>
                            {completedOrders.map((order) => (
                                <Chip
                                    key={order.id}
                                    size={compact ? 'small' : 'medium'}
                                    variant="outlined"
                                    label={`#${getOrderShortNumber(order)} · ${
                                        order.customer?.profile?.name || 'Sin cliente'
                                    }`}
                                    onClick={() => onAction(order, 'openInList')}
                                />
                            ))}
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default OrdersBoardView;
